import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  message, 
  Modal, 
  Tag, 
  Statistic, 
  Row, 
  Col, 
  Tabs,
  Popconfirm,
  Tooltip,
  Badge,
  Empty,
  Descriptions,
  Divider
} from 'antd';
import { 
  MessageOutlined, 
  UserOutlined, 
  SearchOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  FileTextOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API from '../services/api';
import './AdminAIChatManagement.css';

const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;

const AdminAIChatManagement = () => {
  // 会话列表状态
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPagination, setSessionsPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [sessionsFilters, setSessionsFilters] = useState({
    userId: '',
    search: '',
    sortBy: 'updatedAt',
    sortOrder: 'desc'
  });

  // 消息详情状态
  const [messagesModalVisible, setMessagesModalVisible] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesPagination, setMessagesPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  // 统计数据状态
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsDateRange, setStatisticsDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  // 搜索状态
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPagination, setSearchPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [searchFilters, setSearchFilters] = useState({
    keyword: '',
    userId: '',
    intent: '',
    startDate: null,
    endDate: null
  });

  // 选中的会话
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);

  // 当前激活的标签页
  const [activeTab, setActiveTab] = useState('sessions');

  // 获取会话列表
  const fetchSessions = useCallback(async (page, pageSize) => {
    setSessionsLoading(true);
    try {
      const params = {
        page: page || 1,
        limit: pageSize || 20,
        sortBy: sessionsFilters.sortBy,
        sortOrder: sessionsFilters.sortOrder
      };
      if (sessionsFilters.userId) params.userId = sessionsFilters.userId;
      if (sessionsFilters.search) params.search = sessionsFilters.search;

      const response = await API.get('/api/admin/ai-chat/sessions', { params });
      if (response.data.success) {
        setSessions(response.data.data.list || response.data.data.sessions || []);
        setSessionsPagination({
          current: response.data.data.page || response.data.data.pagination?.page || 1,
          pageSize: response.data.data.limit || response.data.data.pagination?.limit || 20,
          total: response.data.data.total || response.data.data.pagination?.total || 0
        });
      }
    } catch (error) {
      message.error('获取会话列表失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSessionsLoading(false);
    }
  }, [sessionsFilters]);

  // 获取会话消息
  const fetchSessionMessages = async (sessionId, page = 1, pageSize = 50) => {
    setMessagesLoading(true);
    try {
      const response = await API.get(`/api/admin/ai-chat/sessions/${sessionId}/messages`, {
        params: { page, limit: pageSize }
      });
      if (response.data.success) {
        setCurrentSession(response.data.data.session);
        setMessages(response.data.data.messages);
        setMessagesPagination({
          current: response.data.data.pagination.page,
          pageSize: response.data.data.pagination.limit,
          total: response.data.data.pagination.total
        });
      }
    } catch (error) {
      message.error('获取消息失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setMessagesLoading(false);
    }
  };

  // 获取统计数据
  const fetchStatistics = useCallback(async () => {
    setStatisticsLoading(true);
    try {
      const params = {};
      if (statisticsDateRange && statisticsDateRange.length === 2) {
        params.startDate = statisticsDateRange[0].format('YYYY-MM-DD');
        params.endDate = statisticsDateRange[1].format('YYYY-MM-DD');
      }

      const response = await API.get('/api/admin/ai-chat/statistics', { params });
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      message.error('获取统计数据失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setStatisticsLoading(false);
    }
  }, [statisticsDateRange]);

  // 搜索消息
  const handleSearch = async (page = 1, pageSize = 20) => {
    if (!searchFilters.keyword.trim()) {
      message.warning('请输入搜索关键词');
      return;
    }

    setSearchLoading(true);
    try {
      const params = {
        keyword: searchFilters.keyword,
        page,
        limit: pageSize
      };
      if (searchFilters.userId) params.userId = searchFilters.userId;
      if (searchFilters.intent) params.intent = searchFilters.intent;
      if (searchFilters.startDate) params.startDate = searchFilters.startDate;
      if (searchFilters.endDate) params.endDate = searchFilters.endDate;

      const response = await API.get('/api/admin/ai-chat/search', { params });
      if (response.data.success) {
        setSearchResults(response.data.data.messages);
        setSearchPagination({
          current: response.data.data.pagination.page,
          pageSize: response.data.data.pagination.limit,
          total: response.data.data.pagination.total
        });
      }
    } catch (error) {
      message.error('搜索失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSearchLoading(false);
    }
  };

  // 删除会话
  const handleDeleteSession = async (sessionId) => {
    try {
      const response = await API.delete(`/api/admin/ai-chat/sessions/${sessionId}`);
      if (response.data.success) {
        message.success('会话删除成功');
        
        // 如果当前页只有一条数据且不是第一页，删除后跳转到前一页
        const currentPage = sessionsPagination.current;
        const isLastItemOnPage = sessions.length === 1;
        const isNotFirstPage = currentPage > 1;
        
        if (isLastItemOnPage && isNotFirstPage) {
          fetchSessions(currentPage - 1, sessionsPagination.pageSize);
        } else {
          fetchSessions(currentPage, sessionsPagination.pageSize);
        }
      }
    } catch (error) {
      message.error('删除失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 批量删除会话
  const handleBatchDelete = async () => {
    if (selectedSessionIds.length === 0) {
      message.warning('请选择要删除的会话');
      return;
    }

    try {
      const response = await API.post('/api/admin/ai-chat/sessions/batch-delete', {
        sessionIds: selectedSessionIds
      });
      if (response.data.success) {
        message.success(`成功删除 ${selectedSessionIds.length} 个会话`);
        setSelectedSessionIds([]);
        
        // 如果删除的数量等于当前页的数量且不是第一页，跳转到前一页
        const currentPage = sessionsPagination.current;
        const isAllItemsDeleted = selectedSessionIds.length >= sessions.length;
        const isNotFirstPage = currentPage > 1;
        
        if (isAllItemsDeleted && isNotFirstPage) {
          fetchSessions(currentPage - 1, sessionsPagination.pageSize);
        } else {
          fetchSessions(currentPage, sessionsPagination.pageSize);
        }
      }
    } catch (error) {
      message.error('批量删除失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 查看会话消息
  const handleViewMessages = (session) => {
    setMessagesModalVisible(true);
    setMessagesPagination({ current: 1, pageSize: 50, total: 0 });
    fetchSessionMessages(session._id);
  };

  // 格式化时间
  const formatTime = (time) => {
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  // 获取意图标签颜色
  const getIntentColor = (intent) => {
    const colors = {
      'query_attack_log': 'blue',
      'block_oversea': 'orange',
      'unblock_oversea': 'green',
      'query_ddos_status': 'purple',
      'general_question': 'default',
      'ip_blackhole_query': 'blue',
      'ip_permission_query': 'green',
      'general_query': 'default',
      'faq': 'purple',
      'processing': 'orange',
      'unknown': 'red'
    };
    return colors[intent] || 'default';
  };

  // 获取意图中文名
  const getIntentName = (intent) => {
    const names = {
      'query_attack_log': '查询攻击日志',
      'block_oversea': '封禁海外',
      'unblock_oversea': '解封海外',
      'query_ddos_status': '查询DDoS状态',
      'general_question': '一般问题',
      'ip_blackhole_query': 'IP黑洞查询',
      'ip_permission_query': 'IP权限查询',
      'general_query': '一般查询',
      'faq': '常见问题',
      'processing': '处理中',
      'unknown': '未知'
    };
    return names[intent] || intent;
  };

  // 会话列表表格列
  const sessionColumns = [
    {
      title: '会话ID',
      dataIndex: '_id',
      key: '_id',
      width: 100,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {text.substring(0, 8)}...
          </span>
        </Tooltip>
      )
    },
    {
      title: '会话标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true
    },
    {
      title: '用户',
      dataIndex: 'userId',
      key: 'userId',
      width: 150,
      render: (userId) => (
        <Space>
          <UserOutlined />
          <span>{userId?.username || '未知用户'}</span>
        </Space>
      )
    },
    {
      title: '消息数',
      dataIndex: 'messageCount',
      key: 'messageCount',
      width: 80,
      align: 'center',
      render: (count) => <Badge count={count} showZero style={{ backgroundColor: '#52c41a' }} />
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 80,
      align: 'center',
      render: (source) => (
        <Tag color={source === 'api' ? 'blue' : 'default'}>
          {source === 'api' ? 'API' : source === 'web' ? 'Web' : '未知'}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '活跃' : '已归档'}
        </Tag>
      )
    },
    {
      title: '最后消息',
      dataIndex: 'lastMessage',
      key: 'lastMessage',
      width: 250,
      ellipsis: true,
      render: (lastMessage) => lastMessage?.content || '-'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time) => formatTime(time)
    },
    {
      title: '最后活动',
      dataIndex: 'lastMessageAt',
      key: 'lastMessageAt',
      width: 160,
      render: (time) => formatTime(time)
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewMessages(record)}
            size="small"
          >
            查看
          </Button>
          <Popconfirm
            title="确定要删除这个会话吗？"
            description="删除后将无法恢复，包括所有消息记录"
            onConfirm={() => handleDeleteSession(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 搜索结果表格列
  const searchColumns = [
    {
      title: '消息内容',
      dataIndex: 'content',
      key: 'content',
      width: 300,
      ellipsis: true
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 80,
      render: (role) => (
        <Tag color={role === 'user' ? 'blue' : 'green'}>
          {role === 'user' ? '用户' : 'AI'}
        </Tag>
      )
    },
    {
      title: '意图',
      dataIndex: 'intent',
      key: 'intent',
      width: 120,
      render: (intent) => intent ? (
        <Tag color={getIntentColor(intent)}>{getIntentName(intent)}</Tag>
      ) : '-'
    },
    {
      title: '用户',
      key: 'username',
      width: 120,
      render: (_, record) => record.session?.userId?.username || record.session?.user?.username || '-'
    },
    {
      title: '会话标题',
      dataIndex: ['session', 'title'],
      key: 'sessionTitle',
      width: 200,
      ellipsis: true
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time) => formatTime(time)
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewMessages(record.session)}
          size="small"
        >
          查看会话
        </Button>
      )
    }
  ];

  // 初始加载
  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchSessions(1, 20);
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="admin-ai-chat-management">
      <Card 
        title={
          <Space>
            <RobotOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <span>AI助手聊天记录管理</span>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* 会话列表 */}
          <TabPane 
            tab={
              <span>
                <MessageOutlined />
                会话列表
              </span>
            } 
            key="sessions"
          >
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Search
                    placeholder="搜索会话标题"
                    allowClear
                    onSearch={(value) => {
                      setSessionsFilters({ ...sessionsFilters, search: value });
                      setSessionsPagination({ ...sessionsPagination, current: 1 });
                    }}
                    onChange={(e) => {
                      if (!e.target.value) {
                        setSessionsFilters({ ...sessionsFilters, search: '' });
                      }
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Input
                    placeholder="用户ID筛选"
                    allowClear
                    onChange={(e) => {
                      setSessionsFilters({ ...sessionsFilters, userId: e.target.value });
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="排序方式"
                    value={sessionsFilters.sortBy}
                    onChange={(value) => {
                      setSessionsFilters({ ...sessionsFilters, sortBy: value });
                    }}
                  >
                    <Select.Option value="updatedAt">最后活动</Select.Option>
                    <Select.Option value="createdAt">创建时间</Select.Option>
                    <Select.Option value="messageCount">消息数量</Select.Option>
                  </Select>
                </Col>
                <Col span={4}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="排序方向"
                    value={sessionsFilters.sortOrder}
                    onChange={(value) => {
                      setSessionsFilters({ ...sessionsFilters, sortOrder: value });
                    }}
                  >
                    <Select.Option value="desc">降序</Select.Option>
                    <Select.Option value="asc">升序</Select.Option>
                  </Select>
                </Col>
                <Col span={4}>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => fetchSessions(1)}
                    block
                  >
                    查询
                  </Button>
                </Col>
              </Row>
              {selectedSessionIds.length > 0 && (
                <Row>
                  <Col span={24}>
                    <Space>
                      <span>已选择 {selectedSessionIds.length} 个会话</span>
                      <Popconfirm
                        title="批量删除会话"
                        description={`确定要删除选中的 ${selectedSessionIds.length} 个会话吗？`}
                        onConfirm={handleBatchDelete}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          批量删除
                        </Button>
                      </Popconfirm>
                      <Button onClick={() => setSelectedSessionIds([])}>
                        取消选择
                      </Button>
                    </Space>
                  </Col>
                </Row>
              )}
            </Space>

            <Table
              columns={sessionColumns}
              dataSource={sessions}
              loading={sessionsLoading}
              rowKey="_id"
              scroll={{ x: 1600 }}
              rowSelection={{
                selectedRowKeys: selectedSessionIds,
                onChange: (selectedKeys) => setSelectedSessionIds(selectedKeys)
              }}
              pagination={{
                ...sessionsPagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  fetchSessions(page, pageSize);
                }
              }}
            />
          </TabPane>

          {/* 统计分析 */}
          <TabPane 
            tab={
              <span>
                <BarChartOutlined />
                统计分析
              </span>
            } 
            key="statistics"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Row gutter={16} align="middle">
                <Col span={12}>
                  <RangePicker
                    value={statisticsDateRange}
                    onChange={setStatisticsDateRange}
                    format="YYYY-MM-DD"
                  />
                </Col>
                <Col span={12}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={fetchStatistics}
                      loading={statisticsLoading}
                    >
                      查询统计
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        setStatisticsDateRange([dayjs().subtract(30, 'days'), dayjs()]);
                      }}
                    >
                      重置
                    </Button>
                  </Space>
                </Col>
              </Row>

              {statistics && (
                <>
                  {/* 总览统计 */}
                  <Card title="总览统计" size="small">
                    <Row gutter={16}>
                      <Col span={6}>
                        <Statistic
                          title="总会话数"
                          value={statistics.overview?.totalSessions || 0}
                          prefix={<MessageOutlined />}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="活跃会话数"
                          value={statistics.overview?.activeSessions || 0}
                          prefix={<ClockCircleOutlined />}
                          valueStyle={{ color: '#3f8600' }}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="总消息数"
                          value={statistics.overview?.totalMessages || 0}
                          prefix={<FileTextOutlined />}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="平均消息数"
                          value={statistics.overview?.averageMessagesPerSession || statistics.overview?.avgMessagesPerSession || 0}
                          precision={2}
                          prefix={<BarChartOutlined />}
                        />
                      </Col>
                    </Row>
                  </Card>

                  {/* Top 10 活跃用户 */}
                  <Card title="Top 10 活跃用户" size="small">
                    <Table
                      dataSource={statistics.topUsers || []}
                      rowKey="userId"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: '排名',
                          key: 'rank',
                          width: 60,
                          render: (_, __, index) => index + 1
                        },
                        {
                          title: '用户',
                          dataIndex: 'username',
                          key: 'username'
                        },
                        {
                          title: '邮箱',
                          dataIndex: 'email',
                          key: 'email'
                        },
                        {
                          title: '会话数',
                          dataIndex: 'sessionCount',
                          key: 'sessionCount',
                          align: 'center'
                        },
                        {
                          title: '最后活动',
                          dataIndex: 'lastActivity',
                          key: 'lastActivity',
                          render: (time) => formatTime(time)
                        }
                      ]}
                    />
                  </Card>

                  {/* 意图分布 */}
                  <Card title="意图分布统计" size="small">
                    <Row gutter={16}>
                      {statistics.intentDistribution?.map((item) => (
                        <Col span={6} key={item._id}>
                          <Card size="small">
                            <Statistic
                              title={getIntentName(item._id)}
                              value={item.count}
                              suffix={
                                <span style={{ fontSize: '14px', color: '#999' }}>
                                  ({((item.count / statistics.overview?.totalMessages) * 100).toFixed(1)}%)
                                </span>
                              }
                            />
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Card>

                  {/* 每日活动统计 */}
                  {statistics.dailyActivity && statistics.dailyActivity.length > 0 && (
                    <Card title="每日活动统计" size="small">
                      <Table
                        dataSource={statistics.dailyActivity}
                        rowKey="_id"
                        pagination={false}
                        size="small"
                        scroll={{ y: 400 }}
                        columns={[
                          {
                            title: '日期',
                            dataIndex: '_id',
                            key: '_id',
                            render: (date) => dayjs(date).format('YYYY-MM-DD')
                          },
                          {
                            title: '消息数',
                            dataIndex: 'messageCount',
                            key: 'messageCount',
                            align: 'center'
                          }
                        ]}
                      />
                    </Card>
                  )}
                </>
              )}

              {!statistics && !statisticsLoading && (
                <Empty description="请选择日期范围并点击查询" />
              )}
            </Space>
          </TabPane>

          {/* 消息搜索 */}
          <TabPane 
            tab={
              <span>
                <SearchOutlined />
                消息搜索
              </span>
            } 
            key="search"
          >
            <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Search
                    placeholder="搜索消息内容（必填）"
                    allowClear
                    value={searchFilters.keyword}
                    onChange={(e) => setSearchFilters({ ...searchFilters, keyword: e.target.value })}
                    onSearch={() => handleSearch(1)}
                    enterButton="搜索"
                  />
                </Col>
                <Col span={4}>
                  <Input
                    placeholder="用户ID筛选"
                    allowClear
                    value={searchFilters.userId}
                    onChange={(e) => setSearchFilters({ ...searchFilters, userId: e.target.value })}
                  />
                </Col>
                <Col span={4}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="意图筛选"
                    allowClear
                    value={searchFilters.intent}
                    onChange={(value) => setSearchFilters({ ...searchFilters, intent: value })}
                  >
                    <Select.Option value="query_attack_log">查询攻击日志</Select.Option>
                    <Select.Option value="block_oversea">封禁海外</Select.Option>
                    <Select.Option value="unblock_oversea">解封海外</Select.Option>
                    <Select.Option value="query_ddos_status">查询DDoS状态</Select.Option>
                    <Select.Option value="general_question">一般问题</Select.Option>
                    <Select.Option value="unknown">未知</Select.Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <RangePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    onChange={(dates) => {
                      if (dates) {
                        setSearchFilters({
                          ...searchFilters,
                          startDate: dates[0].format('YYYY-MM-DD'),
                          endDate: dates[1].format('YYYY-MM-DD')
                        });
                      } else {
                        setSearchFilters({
                          ...searchFilters,
                          startDate: null,
                          endDate: null
                        });
                      }
                    }}
                  />
                </Col>
              </Row>
            </Space>

            <Table
              columns={searchColumns}
              dataSource={searchResults}
              loading={searchLoading}
              rowKey="_id"
              scroll={{ x: 1200 }}
              pagination={{
                ...searchPagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  handleSearch(page, pageSize);
                }
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 消息详情弹窗 */}
      <Modal
        title={
          currentSession && (
            <Space>
              <MessageOutlined />
              <span>会话消息详情</span>
            </Space>
          )
        }
        open={messagesModalVisible}
        onCancel={() => {
          setMessagesModalVisible(false);
          setCurrentSession(null);
          setMessages([]);
        }}
        width={1200}
        footer={null}
        destroyOnClose
      >
        {currentSession && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="会话ID">
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {currentSession._id}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="会话标题">
                {currentSession.title}
              </Descriptions.Item>
              <Descriptions.Item label="用户">
                {currentSession.userId?.username || currentSession.user?.username || '未知用户'}
              </Descriptions.Item>
              <Descriptions.Item label="用户邮箱">
                {currentSession.userId?.email || currentSession.user?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="消息数量">
                <Badge count={currentSession.messageCount} showZero />
              </Descriptions.Item>
              <Descriptions.Item label="来源">
                <Tag color={currentSession.source === 'api' ? 'blue' : 'default'}>
                  {currentSession.source === 'api' ? 'API' : currentSession.source === 'web' ? 'Web' : '未知'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={currentSession.status === 'active' ? 'green' : 'default'}>
                  {currentSession.status === 'active' ? '活跃' : '已归档'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {formatTime(currentSession.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="最后活动">
                {formatTime(currentSession.lastMessageAt)}
              </Descriptions.Item>
            </Descriptions>

            <Divider>消息记录</Divider>

            <div className="messages-list-container">
              {messages.map((msg, index) => (
                <div key={msg._id} className={`message-item ${msg.role}`}>
                  <div className="message-header">
                    <Space>
                      {msg.role === 'user' ? (
                        <Badge color="blue" text="用户" />
                      ) : (
                        <Badge color="green" text="AI助手" />
                      )}
                      {msg.intent && (
                        <Tag color={getIntentColor(msg.intent)}>
                          {getIntentName(msg.intent)}
                        </Tag>
                      )}
                      <span style={{ color: '#999', fontSize: '12px' }}>
                        {formatTime(msg.createdAt)}
                      </span>
                    </Space>
                  </div>
                  
                  <div className="message-content">
                    {msg.role === 'assistant' ? (
                      <div className="assistant-message">
                        <div className="markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                        {(msg.matchedKnowledgeId || msg.apiResult) && (
                          <div className="message-meta">
                            <Space size="large">
                              {msg.matchedKnowledgeId && (
                                <span>
                                  <span style={{ color: '#999' }}>知识库ID：</span>
                                  <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                    {msg.matchedKnowledgeId}
                                  </span>
                                </span>
                              )}
                              {msg.apiResult && (
                                <span>
                                  <span style={{ color: '#999' }}>API调用：</span>
                                  <Tag color={msg.apiResult.success ? 'success' : 'error'} size="small">
                                    {msg.apiResult.success ? '成功' : '失败'}
                                  </Tag>
                                </span>
                              )}
                            </Space>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="user-message">
                        {msg.content}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {messagesPagination.total > messages.length && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button
                    onClick={() => fetchSessionMessages(
                      currentSession._id,
                      messagesPagination.current + 1,
                      messagesPagination.pageSize
                    )}
                    loading={messagesLoading}
                  >
                    加载更多消息 ({messages.length}/{messagesPagination.total})
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminAIChatManagement;

